import { useState } from 'react';
import { Check, Copy, Download, Plus } from 'lucide-react';

const inp = 'h-9 rounded-lg border border-slate-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400';

const AddSource = ({ api, onAdded }) => {
  const [f, setF] = useState({ name: '', type: 'campus_ambassador', college: '', code: '' });
  const [err, setErr] = useState('');
  const add = async () => {
    setErr('');
    const res = await api('/sources', { method: 'POST', body: JSON.stringify({ ...f, code: f.code || null }) }, true);
    if (res?.error) setErr(res.error); else { setF({ name: '', type: f.type, college: '', code: '' }); onAdded(); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center bg-slate-50 rounded-xl p-3" data-testid="referral-add-form">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={`${inp} flex-1 min-w-[160px]`} data-testid="referral-add-name" />
      <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inp} data-testid="referral-add-type">
        <option value="campus_ambassador">Campus Ambassador</option><option value="internal_team">Internal Team</option>
      </select>
      <input value={f.college} onChange={(e) => setF({ ...f, college: e.target.value })} placeholder="College / institution (optional)" className={`${inp} flex-1 min-w-[180px]`} data-testid="referral-add-college" />
      <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="Code (auto)" className={`${inp} w-28`} data-testid="referral-add-code" />
      <button type="button" onClick={add} disabled={!f.name.trim()} className="inline-flex items-center gap-1 rounded-full bg-[#0b1f4d] text-white text-sm font-semibold px-4 py-2 disabled:opacity-40" data-testid="referral-add-btn"><Plus className="w-4 h-4" />Add</button>
      {err && <p className="w-full text-xs text-rose-600" data-testid="referral-add-error">{err}</p>}
    </div>
  );
};

const SourceRow = ({ s, api, authFetch, apiUrl, canEdit, onChanged }) => {
  const [name, setName] = useState(s.name);
  const [college, setCollege] = useState(s.college);
  const [copied, setCopied] = useState(false);
  const dirty = name !== s.name || college !== s.college;
  const patch = async (body) => {
    const res = await api(`/sources/${s.code}`, { method: 'PATCH', body: JSON.stringify(body) }, true);
    if (res?.error) window.alert(`Could not save: ${res.error}`);
    onChanged();
  };
  const copy = () => { navigator.clipboard?.writeText(s.url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const qr = async () => {
    const res = await authFetch(`${apiUrl}/api/admin/referrals/sources/${s.code}/qr.png`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `elevate-referral-${s.code}.png`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <tr className={`border-b border-slate-100 ${s.active ? '' : 'opacity-60'}`} data-testid={`referral-source-${s.code}`}>
      <td className="px-3 py-2.5 font-mono font-semibold text-[#0b1f4d]">{s.code}</td>
      <td className="px-3 py-2.5"><input disabled={!canEdit} value={name} onChange={(e) => setName(e.target.value)} className={`${inp} w-full`} data-testid={`referral-name-${s.code}`} /></td>
      <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{s.type_label}</td>
      <td className="px-3 py-2.5"><input disabled={!canEdit} value={college} onChange={(e) => setCollege(e.target.value)} placeholder="—" className={`${inp} w-full`} data-testid={`referral-college-${s.code}`} /></td>
      <td className="px-3 py-2.5 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500 truncate max-w-[220px]" title={s.url}>{s.url.replace('https://', '')}</span>
          <button type="button" onClick={copy} title="Copy link" className="text-slate-500 hover:text-teal-700" data-testid={`referral-copy-${s.code}`}>{copied ? <Check className="w-4 h-4 text-teal-600" /> : <Copy className="w-4 h-4" />}</button>
          <button type="button" onClick={qr} title="Download QR code" className="text-slate-500 hover:text-teal-700" data-testid={`referral-qr-${s.code}`}><Download className="w-4 h-4" /></button>
        </div>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        {canEdit && dirty && <button type="button" onClick={() => patch({ name, college })} className="rounded-full bg-teal-600 text-white text-xs font-semibold px-3 py-1 mr-2" data-testid={`referral-save-${s.code}`}>Save</button>}
        {canEdit ? (
          <button type="button" onClick={() => patch({ active: !s.active })} data-testid={`referral-toggle-${s.code}`}
            className={`rounded-full text-xs font-semibold px-3 py-1 ${s.active ? 'bg-teal-100 text-teal-800' : 'bg-slate-200 text-slate-600'}`}>{s.active ? 'Active' : 'Inactive'}</button>
        ) : <span className="text-xs">{s.active ? 'Active' : 'Inactive'}</span>}
      </td>
    </tr>
  );
};

export const ReferralSources = ({ sources, api, authFetch, apiUrl, canEdit, onChanged }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="referral-sources">
    <div>
      <h3 className="font-outfit text-lg font-bold text-[#0b1f4d]">Referral sources</h3>
      <p className="text-xs text-slate-500">Each person shares their own Talent Network link or QR code. Deactivating a link stops new attribution but keeps everyone they already brought in.</p>
    </div>
    {canEdit && <AddSource api={api} onAdded={onChanged} />}
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[860px]">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
          <tr>{['Code', 'Name', 'Type', 'College', 'Share link / QR', 'Status'].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>{sources.map((s) => <SourceRow key={`${s.code}-${s.updated_at}`} s={s} api={api} authFetch={authFetch} apiUrl={apiUrl} canEdit={canEdit} onChanged={onChanged} />)}</tbody>
      </table>
    </div>
  </div>
);
